import SpectrumChart from 'features/spectrumChart/SpectrumChart';
import {
  LiveIcon,
  LockIcon,
  PollingIcon,
  TimeshareIcon,
} from '../../components/ModeIcons';

import { Analyzer, Snapshot } from 'types';
import { styled } from '@mui/material';
import { useAppSelector } from '../../app/hooks';
import { Grid } from '@mui/material';
import CenteredCircularProgress from 'components/CenteredCircularProgress';
import Spectrogram from 'features/spectrogram/Spectrogram';
import WeatherAlertIcon from './WeatherAlertIcon';
import { WindowForm } from 'features/wallOrganizer/types';

import { useWall } from 'features/walls/api/getWall';
import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useUsers } from 'features/userManagement/api/getUsers';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import { useIsSnapshotAlarming } from './hooks/useIsSnapshotAlarming';
import TrendlineChart from 'features/trendline/TrendlineChart';

interface Props {
  analyzer: Analyzer | undefined;
  snapshot: Snapshot | undefined;
  hideLabels?: boolean;
}

const ChartLabel = ({ analyzer, snapshot, hideLabels }: Props) => {
  const { data: users } = useUsers({});
  const { user } = useAppSelector((state) => state.auth);

  const usersPlusCurrentUser = [...(users || []), user];
  const lockUser = usersPlusCurrentUser.find(
    (user) => snapshot?.lock_user_id === user?.id
  );
  if (hideLabels) return null;
  const activeLiveSnapshot = analyzer?.active_snapshot === snapshot?.id;
  return (
    <Grid container style={{ width: '100%' }} justifyContent='space-between'>
      <Grid item container>
        {analyzer?.mode === 0 && (
          <LiveIcon enabled={activeLiveSnapshot} fontSize='small' />
        )}
        {analyzer?.mode === 1 && !analyzer?.in_time_share && (
          <PollingIcon enabled={snapshot?.enabled} fontSize='small' />
        )}
        {analyzer?.mode === 1 && analyzer?.in_time_share && (
          <TimeshareIcon enabled={snapshot?.enabled} fontSize='small' />
        )}
        {snapshot?.lock_user_id && (
          <LockIcon fontSize='small' lockUser={lockUser} />
        )}
        <Grid item style={{ paddingLeft: '.3rem', height: '20px' }}>
          <WeatherAlertIcon analyzerId={analyzer?.id ? analyzer.id : -1} />
        </Grid>
      </Grid>
    </Grid>
  );
};

interface WindowProps {
  wallId: number;
  row: number;
  column: number;
  handleWindowClicked: (row: number, column: number) => void;
  disableTraces?: boolean;
  windowsOverride?: WindowForm[];
}

interface StyledWindowProps {
  borderColor: string | null | undefined;
  snapshotIsAlarming: boolean | undefined;
  warning: boolean | undefined;
}

const StyledWindow = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'borderColor' &&
    prop !== 'snapshotIsAlarming' &&
    prop !== 'warning',
})<StyledWindowProps>(
  ({ theme, borderColor, snapshotIsAlarming, warning }) => ({
    '@keyframes alarmingAnimation': {
      '0%': {
        opacity: 0,
      },
      '50%': {
        opacity: 1,
      },
      '100%': {
        opacity: 0,
      },
    },
    ...(!snapshotIsAlarming &&
      !warning && {
        borderRadius: '10px',
        boxShadow: borderColor
          ? 'inset 0 0 0 7px ' + borderColor
          : 'inset 0 0 0 0px transparent',
      }),
    ...(snapshotIsAlarming &&
      !warning && {
        borderRadius: '10px',
        position: 'relative',
        // Use a pseudoclass for the animation to improve performance
        '&:after': {
          borderRadius: '10px',
          boxShadow: 'inset 0 0 0 7px red',
          position: 'absolute',
          content: '""',
          animation: 'alarmingAnimation 1s',
          animationIterationCount: 'infinite',
          height: '100%',
          width: '100%',
          top: 0,
          left: 0,
          willChange: 'opacity',
        },
      }),
    ...(snapshotIsAlarming &&
      warning && {
        borderRadius: '10px',
        position: 'relative',
        // Use a pseudoclass for the animation to improve performance
        '&:after': {
          borderRadius: '10px',
          boxShadow: 'inset 0 0 0 7px red',
          position: 'absolute',
          content: '""',
          animation: 'alarmingAnimation 1s',
          animationIterationCount: 'infinite',
          height: '100%',
          width: '100%',
          top: 0,
          left: 0,
          willChange: 'opacity',
        },
      }),
  })
);

const WindowComponent = ({
  wallId,
  row,
  column,
  handleWindowClicked,
  disableTraces,
  windowsOverride,
}: WindowProps) => {
  const { data: wall } = useWall({ wallId });
  const wallWindows = windowsOverride ? windowsOverride : wall?.windows;

  const foundWindow = wallWindows?.find(
    (window) => window.pos_x === row && window.pos_y === column
  );
  const analyzerId = foundWindow ? foundWindow.analyzer_id : -1;

  const { data: analyzer, isLoading: analyzerIsLoading } = useAnalyzer({
    analyzerId,
  });

  const liveSnapshotId = analyzer ? analyzer.active_snapshot : -1;
  const snapshotId = foundWindow?.snapshot_id
    ? foundWindow.snapshot_id
    : liveSnapshotId;
  const { data: snapshot, isLoading: snapshotIsLoading } = useSnapshot({
    snapshotId,
  });
  const { snapshotIsAlarming, warning } = useIsSnapshotAlarming(snapshotId);

  const handleClicked = () => {
    handleWindowClicked(row, column);
  };

  if (analyzerIsLoading || snapshotIsLoading)
    return <CenteredCircularProgress />;

  if (
    foundWindow &&
    foundWindow.analyzer_id &&
    analyzer &&
    snapshotId !== undefined &&
    snapshot
  ) {
    if (foundWindow.trace_mode === 0) {
      return (
        <StyledWindow
          snapshotIsAlarming={snapshotIsAlarming}
          warning={warning}
          borderColor={snapshot?.border_color}
          style={{
            padding: '.2rem',
            borderRadius: '10px',
            height: '100%',
          }}
          onClick={handleClicked}
        >
          <ChartLabel
            analyzer={analyzer}
            snapshot={snapshot}
            hideLabels={!!wall?.hide_labels}
          />
          <SpectrumChart
            analyzerId={analyzerId}
            snapshotId={snapshotId}
            hideLabels={!!wall?.hide_labels}
            hideDetectorNames={!!wall?.hide_detector_names}
            disableTraces={disableTraces}
          />
        </StyledWindow>
      );
    } else if (foundWindow.trace_mode === 1) {
      return (
        <StyledWindow
          snapshotIsAlarming={snapshotIsAlarming}
          warning={warning}
          borderColor={snapshot?.border_color}
          style={{
            padding: '.2rem',
            borderRadius: '10px',
          }}
          onClick={handleClicked}
        >
          <ChartLabel
            analyzer={analyzer}
            snapshot={snapshot}
            hideLabels={!!wall?.hide_labels}
          />
          <Spectrogram
            analyzerId={foundWindow.analyzer_id}
            snapshotId={snapshotId}
            hideLabels={!!wall?.hide_labels}
          />
        </StyledWindow>
      );
    } else if (foundWindow.trace_mode === 2) {
      return (
        <div
          style={{
            padding: '.2rem',
            borderRadius: '10px',
            height: '100%',
            width: '100%',
          }}
          onClick={handleClicked}
        >
          <TrendlineChart snapshotId={foundWindow.snapshot_id} interval='Day' />
        </div>
      );
    }
  }

  return (
    <div
      style={{
        padding: '.2rem',
        borderRadius: '10px',
      }}
      onClick={handleClicked}
    >
      <ChartLabel
        analyzer={analyzer}
        snapshot={snapshot}
        hideLabels={!!wall?.hide_labels}
      />

      <SpectrumChart analyzerId={-1} snapshotId={-1} />
    </div>
  );
};

export default WindowComponent;
